import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { sessionExpiredHandlerRef } from '../services/api';
import { clearAuthCaches } from '../lib/cache';
import { validateAuthIdentity, AUTH_VERIFICATION_KINDS } from '../lib/authIdentity';

const AuthContext = createContext(null);
const AUTH_RESTORE_TIMEOUT_MS = 10000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('adamsched_token'));
  const [loading, setLoading] = useState(true);
  const [language, setLanguageState] = useState(localStorage.getItem('adamsched_language') || 'es');
  const [impersonatingCenter, setImpersonatingCenter] = useState(
    sessionStorage.getItem('impersonating_center') || null
  );

  // ============================================================================
  // authenticated-cache-isolation (Leaf E) — security state
  // ============================================================================
  // cacheSecurityError: { source: 'login'|'logout'|'switchToken'|'restoreSuperAdmin', detail: string } | null
  // setCacheSecurityError is PRIVATE — only the component itself can set it,
  // never any consumer (fail-closed invariant).
  const [cacheSecurityError, setCacheSecurityError] = useState(null);
  const [authVerificationError, setAuthVerificationError] = useState(null);
  const authGenerationRef = useRef(0);
  const restoreControllerRef = useRef(null);

  // ============================================================================
  // Helpers (private)
  // ============================================================================

  // Hard-invalidate ALL locally-stored credentials. Called whenever a
  // cache-purge fails AND there is no path that lets us safely
  // continue with the previous identity. After this call, no
  // authenticated request can be sent (no token, no Authorization).
  const invalidateLocalCredentials = useCallback(() => {
    sessionStorage.removeItem('adamsched_token');
    sessionStorage.removeItem('superadmin_token');
    sessionStorage.removeItem('impersonating_center');
    setToken(null);
    setUser(null);
    setImpersonatingCenter(null);
  }, []);

  // ============================================================================
  // Public actions exposed via context
  // ============================================================================

  // retryCacheCleanup: explicit user action. Clears the cache and,
  // on success, dismisses the security error so /login can render
  // the form again. On failure, keeps the error and propagates.
  const retryCacheCleanup = useCallback(async () => {
    await clearAuthCaches();
    setCacheSecurityError(null);
  }, []);

  const invalidateAuthRequest = useCallback(() => {
    authGenerationRef.current += 1;
    restoreControllerRef.current?.abort();
    restoreControllerRef.current = null;
  }, []);

  // `sessionToken` permite pedir `/auth/me` para un token explícito cuando
  // el estado del token NO cambia (dos logins dentro del mismo segundo
  // producen el MISMO JWT, porque `create_token` redondea `exp` a segundos):
  // en ese caso el efecto no se dispara y la identidad no se validaría nunca.
  const fetchUser = useCallback(async (sessionToken = token) => {
    const generation = authGenerationRef.current + 1;
    authGenerationRef.current = generation;
    restoreControllerRef.current?.abort();
    restoreControllerRef.current = null;

    if (!sessionToken) {
      if (generation === authGenerationRef.current) {
        setLoading(false);
      }
      return;
    }

    const controller = new AbortController();
    restoreControllerRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      if (generation !== authGenerationRef.current) return;
      timedOut = true;
      setAuthVerificationError({
        kind: AUTH_VERIFICATION_KINDS.UNVERIFIED,
        message: 'No hemos podido verificar tu sesión.',
      });
      setLoading(false);
      controller.abort();
    }, AUTH_RESTORE_TIMEOUT_MS);

    setLoading(true);
    setAuthVerificationError(null);

    try {
      const response = await api.get('/auth/me', { signal: controller.signal });
      if (generation !== authGenerationRef.current) return;
      // R5.3/D9: un 2xx NO garantiza una identidad válida. Si el payload no
      // cumple el contrato de roles/tenant, NO se establece `user` y la
      // sesión NO se habilita (`isAuthenticated` sigue en false ⇒
      // `ProtectedRoute` no deja pasar).
      const identity = validateAuthIdentity(response.data);
      if (!identity.valid) {
        // Semántica de credencial: un 2xx con identidad inválida NO es un 401
        // autoritativo ⇒ la credencial se CONSERVA (igual que ante un 5xx o un
        // error de red) y se expone el estado de verificación con reintento.
        // Un backend/proxy que devuelve `{}` no puede destruir una sesión
        // válida; el usuario ve un panel recuperable y puede reintentar.
        console.warn('[auth] /auth/me devolvió una identidad inválida', {
          reason: identity.reason,
        });
        setUser(null);
        // A diferencia de un fallo transitorio, este rechazo es DETERMINISTA:
        // la causa es el contenido del payload, no la conexión. Se distingue
        // con su propio `kind` para que el panel no atribuya el fallo a la red
        // y ofrezca una salida explícita (ver `escapeInvalidIdentity`).
        setAuthVerificationError({
          kind: AUTH_VERIFICATION_KINDS.INVALID_IDENTITY,
          message: 'No hemos podido verificar tu identidad.',
          reason: identity.reason,
        });
        return;
      }
      setUser(identity.user);
      setAuthVerificationError(null);
      if (identity.user.language) {
        setLanguageState(identity.user.language);
        localStorage.setItem('adamsched_language', identity.user.language);
      }
    } catch (error) {
      if (generation !== authGenerationRef.current) return;
      if (controller.signal.aborted) {
        if (timedOut) return;
        return;
      }
      console.error('[auth] /auth/me failed', {
        // R1.2/R5.2: NO se registra el objeto de error de axios: su
        // `config.headers.Authorization` es el JWT y `response.data` son datos
        // personales. Sólo estado y código de motivo.
        status: error?.response?.status ?? 'network',
        code: error?.code || 'unknown',
      });
      // Round-1 fix for review panel finding 7 (D7): we only invalidate
      // the locally stored JWT when the backend has AUTHORITATIVELY told
      // us the credential is invalid (HTTP 401). Network errors
      // (`!error.response`), 5xx, and 403 are kept on the credential so a
      // transient backend hiccup does not force the user to re-login.
      // We surface `authVerificationError` with `kind: 'unverified'` for
      // non-401 cases so the UI can show an explicit retry (see
      // `retryAuth`) instead of silently dropping the session.
      if (error?.response?.status === 401) {
        sessionStorage.removeItem('adamsched_token');
        setToken(null);
        setUser(null);
        setAuthVerificationError(null);
      } else {
        setAuthVerificationError({
          kind: AUTH_VERIFICATION_KINDS.UNVERIFIED,
          message: 'No hemos podido verificar tu sesión.',
        });
        setUser(null);
      }
    } finally {
      clearTimeout(timeoutId);
      if (generation === authGenerationRef.current) {
        restoreControllerRef.current = null;
        setLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
    return invalidateAuthRequest;
  }, [fetchUser, invalidateAuthRequest]);

  // Register the global session-expired handler used by the axios response
  // interceptor (services/api.js). On a 401 with error_code `session_expired`,
  // the interceptor calls this closure: it hard-invalidates local credentials
  // (clears JWT, user, impersonation state) and navigates to /login (D5).
  // The handler is mounted on a module-level ref so it survives HMR and is
  // cleaned up if the provider unmounts (defensive against duplicates).
  useEffect(() => {
    const handler = () => {
      invalidateLocalCredentials();
      navigate('/login');
    };
    sessionExpiredHandlerRef.current = handler;
    return () => {
      if (sessionExpiredHandlerRef.current === handler) {
        sessionExpiredHandlerRef.current = null;
      }
    };
  }, [navigate, invalidateLocalCredentials]);

  const retryAuth = useCallback(() => {
    fetchUser();
  }, [fetchUser]);

  // escapeInvalidIdentity — salida explícita del estado de rechazo
  // DETERMINISTA de identidad (`kind: 'invalid-identity'`).
  //
  // En un rechazo determinista el mismo payload volverá a ser rechazado, así
  // que "Reintentar" no es una salida: el usuario necesita volver a `/login`
  // para introducir credenciales válidas. Se invalida la credencial local y se
  // navega; NO se purga CacheStorage (eso es el camino de `logout`, con su
  // propia semántica de fallo) ni se tocan roles, caches o la API.
  //
  // NO debilita el rechazo: `user` queda null, `isAuthenticated` false y
  // `ProtectedRoute` sigue redirigiendo.
  //
  // Un fallo TRANSITORIO (`unverified`) SÍ puede usar esta acción, pero sólo
  // de forma VOLUNTARIA y explícita del usuario («Cerrar sesión e iniciar
  // sesión de nuevo»): el COMPORTAMIENTO del proveedor no cambia — ante un 5xx
  // o un error de red la credencial se CONSERVA y sólo ante un 401
  // autoritativo se borra. Lo que rechaza el diseño (D6) es un fallback
  // AUTOMÁTICO, no esta salida manual (enmienda de D9, 2026-10-29).
  const escapeInvalidIdentity = useCallback(() => {
    // Cancela cualquier verificación en vuelo para que su respuesta no pueda
    // reinstalar el estado después de la salida.
    invalidateAuthRequest();
    // `invalidateLocalCredentials` ya elimina `adamsched_token`,
    // `superadmin_token` e `impersonating_center` de sessionStorage y pone
    // `token`/`user`/`impersonatingCenter` a null.
    invalidateLocalCredentials();
    setAuthVerificationError(null);
    setLoading(false);
    navigate('/login');
  }, [invalidateAuthRequest, invalidateLocalCredentials, navigate]);

  // ============================================================================
  // login — async with try/catch (D13 + Leaf E)
  // ============================================================================
  // Failure path: clearAuthCaches() rejects. Invalidate local
  // credentials (defensive: previous session, if any, must NOT
  // remain). Surface cacheSecurityError. Throw. POST is never sent.
  const login = async (email, password) => {
    invalidateAuthRequest();
    setAuthVerificationError(null);
    try {
      await clearAuthCaches();
    } catch (err) {
      invalidateLocalCredentials();
      setCacheSecurityError({ source: 'login', detail: err.message });
      throw err;
    }
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    // R5.3/D9 (opción A, autorizada): el payload de `/auth/login` NO es
    // autoritativo para la sesión. El backend puede devolver aquí una
    // identidad INCOMPLETA pero reparable — p. ej. una cuenta CLIENT legacy
    // sin `centerId`, que `get_current_user` auto-repara en `/auth/me`
    // (backend/server.py) antes de responder. Validar este payload y
    // rechazar el login encerraba a esas cuentas en un rechazo
    // determinista que ningún reintento podía resolver.
    //
    // El token se guarda y la sesión queda PROVISIONAL: `user` sigue a
    // null, `isAuthenticated` sigue en false y NINGUNA ruta protegida se
    // monta. La única vía que establece la identidad es `fetchUser()` →
    // `validateAuthIdentity` sobre la respuesta de `/auth/me` — el mismo
    // mecanismo que ya usan `switchToken`/`restoreSuperAdmin`.
    sessionStorage.setItem('adamsched_token', newToken);
    const tokenChanged = newToken !== token;
    setToken(newToken);
    // Mientras `/auth/me` valida, la app muestra el spinner en lugar de
    // expulsar a `/login` (ProtectedRoute: `loading` ⇒ spinner).
    setLoading(true);
    if (!tokenChanged) {
      // El token NO ha cambiado (dos logins en el mismo segundo ⇒ mismo JWT):
      // React no re-renderiza el estado del token, el efecto no se dispara y
      // `/auth/me` no se pediría nunca → la app quedaría en loading
      // perpetuo. Se pide explícitamente para validar la identidad.
      fetchUser(newToken);
    }
    // Nunca se devuelve `undefined`: el consumidor (`Login.js`) lee
    // `userData.role`, y un 2xx sin `user` lo haría explotar con un TypeError
    // en vez de dejar el estado de verificación recuperable.
    return userData && typeof userData === 'object' ? userData : {};
  };

  // ============================================================================
  // logout — async with try/catch (D13 + Leaf E)
  // ============================================================================
  // Invalidate credentials FIRST, then purge caches. If the purge
  // fails, the session is already closed and nothing is restored;
  // surface cacheSecurityError and throw.
  const logout = async () => {
    invalidateAuthRequest();
    setAuthVerificationError(null);
    sessionStorage.removeItem('adamsched_token');
    sessionStorage.removeItem('superadmin_token');
    sessionStorage.removeItem('impersonating_center');
    setToken(null);
    setUser(null);
    setImpersonatingCenter(null);
    setLoading(false);
    try {
      await clearAuthCaches();
    } catch (err) {
      setCacheSecurityError({ source: 'logout', detail: err.message });
      throw err;
    }
  };

  // ============================================================================
  // switchToken — async with fail-closed (D8 + D13)
  // ============================================================================
  // A = current SUPER_ADMIN token (in sessionStorage.adamsched_token).
  // B = newToken (impersonation target).
  // During await clearAuthCaches() A stays in sessionStorage (R1
  // protects any concurrent request). Only after purge succeeds,
  // A is preserved in superadmin_token and B is installed in
  // adamsched_token.
  const switchToken = useCallback(async (newToken, centerName) => {
    invalidateAuthRequest();
    setAuthVerificationError(null);
    try {
      await clearAuthCaches();
    } catch (err) {
      invalidateLocalCredentials();
      setCacheSecurityError({ source: 'switchToken', detail: err.message });
      throw err;
    }
    // Postcondition OK: only now do the swap.
    const currentToken = sessionStorage.getItem('adamsched_token');
    sessionStorage.setItem('superadmin_token', currentToken);
    sessionStorage.setItem('impersonating_center', centerName);
    sessionStorage.setItem('adamsched_token', newToken);
    setImpersonatingCenter(centerName);
    setToken(newToken);
  }, [invalidateAuthRequest, invalidateLocalCredentials]);

  // ============================================================================
  // restoreSuperAdmin — async with fail-closed (D8 + D13)
  // ============================================================================
  // A = savedToken (previously stashed SUPER_ADMIN token).
  // B = the impersonated token currently in adamsched_token.
  // During await clearAuthCaches() B stays in sessionStorage.
  // Only after purge succeeds, B is replaced with A and the
  // impersonation markers are cleared.
  const restoreSuperAdmin = useCallback(async () => {
    invalidateAuthRequest();
    setAuthVerificationError(null);
    const savedToken = sessionStorage.getItem('superadmin_token');
    if (!savedToken) return;
    try {
      await clearAuthCaches();
    } catch (err) {
      invalidateLocalCredentials();
      setCacheSecurityError({ source: 'restoreSuperAdmin', detail: err.message });
      throw err;
    }
    sessionStorage.setItem('adamsched_token', savedToken);
    sessionStorage.removeItem('superadmin_token');
    sessionStorage.removeItem('impersonating_center');
    setImpersonatingCenter(null);
    setToken(savedToken);
  }, [invalidateAuthRequest, invalidateLocalCredentials]);

  const setLanguage = async (lang) => {
    setLanguageState(lang);
    localStorage.setItem('adamsched_language', lang);

    if (token) {
      try {
        await api.put(`/auth/language?language=${lang}`, null);
      } catch (error) {
        // R1.2/R5.2: misma regla de redacción que en `fetchUser` — el objeto
        // de error de axios lleva el JWT en `config.headers.Authorization` y
        // datos personales en `response.data`.
        console.error('[auth] /auth/language failed', {
          status: error?.response?.status ?? 'network',
          code: error?.code || 'unknown',
        });
      }
    }
  };

  const value = {
    user,
    token,
    loading,
    language,
    login,
    logout,
    setLanguage,
    switchToken,
    restoreSuperAdmin,
    impersonatingCenter,
    api,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isCenterAdmin: user?.role === 'CENTER_ADMIN',
    isStaff: user?.role === 'STAFF',
    // authenticated-cache-isolation: only public surface.
    // setCacheSecurityError is intentionally NOT exposed.
    cacheSecurityError,
    retryCacheCleanup,
    authVerificationError,
    retryAuth,
    // Salida explícita del estado de rechazo determinista de identidad
    // (`kind: 'invalid-identity'`). Expuesta como acción pública: el panel
    // de verificación la usa para devolver al usuario a `/login`.
    escapeInvalidIdentity,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
